import { HttpParams } from '@angular/common/http';
import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { EnrichedCartItem, OrderBody } from '@oe-backend/types';
import { firstValueFrom } from 'rxjs';
import { Helper } from 'src/app/helper/helper';
import { CartService } from 'src/app/services/api/cart/cart.service';
import { withLoadingState } from '../loading-state/with-loading-state';
import {
	CartFilters,
	CartOrderTypeFilterEnum,
	CartOtherFilterEnum,
	CartState,
	CartStateFilterEnum,
	CartTimeFilterEnum,
	DateRange,
	Selectable,
} from '../types/cart.type';
import { OrderStore } from './order.store';

export const CartFilterValues = {
	...CartOtherFilterEnum,
	...CartStateFilterEnum,
	...CartTimeFilterEnum,
};

export const CartStore = signalStore(
	{ providedIn: 'root' },
	withState<CartState>({
		itemCount: 0,
		cart: [],
		filters: [],
	}),
	withLoadingState({
		cart: 'init',
		fetchCartItemById: 'init',
		addCartItem: 'init',
		updateCartItem: 'init',
	}),
	withMethods((state) => {
		const cartService = inject(CartService);
		const orderStore = inject(OrderStore);

		//Need it as constant to call it in the other methods
		const fetchCartItems = async () => {
			state.loading('cart');
			let params = new HttpParams();
			if (state.filters().includes(CartStateFilterEnum.Rejected)) {
				params = params.set('status', CartFilterValues.Rejected);
			}
			const dateRange = mapFiltersToDateRange(state.filters());
			if (dateRange) {
				params = params.set('startDate', dateRange.startDate.toISOString());
				params = params.set('endDate', dateRange.endDate.toISOString());
			}
			try {
				const cart = await firstValueFrom(cartService.fetch(params));
				// Initialize isSelected to true for all cart items
				cart.forEach((item: Selectable<EnrichedCartItem>) => {
					item.isSelected = true;
				});
				patchState(state, { cart });
				state.loaded('cart');
			} catch {
				state.errored('cart');
			}
		};

		const fetchCartItemCount = async () => {
			const itemCount = await firstValueFrom(cartService.fetchItemCount());
			patchState(state, { itemCount });
		};

		return {
			fetchCartItems,
			fetchCartItemCount,
			fetchCartItemById: async (id: string) => {
				let cartItem;
				state.loading('fetchCartItemById');
				try {
					cartItem = await firstValueFrom(cartService.fetchById(id));
					state.loaded('fetchCartItemById');
				} catch {
					state.errored('fetchCartItemById');
				}
				return cartItem;
			},
			addCartItem: async (order: OrderBody) => {
				state.loading('addCartItem');
				try {
					await firstValueFrom(cartService.add(order, orderStore.selectedProduct() === 'reader'));
					fetchCartItemCount();
					orderStore.reset();
					state.loaded('addCartItem');
				} catch {
					state.errored('addCartItem');
				}
			},
			updateCartItem: async (id: string, order: OrderBody) => {
				state.loading('updateCartItem');
				try {
					await firstValueFrom(cartService.update(id, order));
					state.loaded('updateCartItem');
				} catch {
					state.errored('updateCartItem');
				}
			},
			setFilters: async (filters: CartFilters[]) => {
				patchState(state, { filters });
				await fetchCartItems();
			},
			removeFilter: async (filter: CartFilters) => {
				patchState(state, { filters: state.filters().filter((f) => f !== filter) });
				await fetchCartItems();
			},
			clearFilters: async () => {
				patchState(state, { filters: [] });
				await fetchCartItems();
			},
			setSelectForAll: (isSelected: boolean) => {
				const updatedCart = state.cart().map((item: Selectable<EnrichedCartItem>) => {
					return {
						...item,
						isSelected: isSelected,
					};
				});
				patchState(state, { cart: updatedCart });
			},
			removeCartItem: async (cartItem: Selectable<EnrichedCartItem>) => {
				if (!cartItem.id) return;
				await firstValueFrom(cartService.delete(cartItem.id));
				fetchCartItemCount();
				const updatedCart = state
					.cart()
					.filter((item: Selectable<EnrichedCartItem>) => item.id !== cartItem.id);
				patchState(state, { cart: updatedCart });
			},
			toggleCheckedCartItem: (cartItem: Selectable<EnrichedCartItem>) => {
				if (!cartItem.id) return;
				const cart = state.cart();
				const updatedCart = cart.map((item: Selectable<EnrichedCartItem>) => {
					if (item.id != cartItem.id) {
						return item;
					}
					return {
						...item,
						isSelected: !item.isSelected,
					};
				});
				patchState(state, { cart: updatedCart });
			},
		};
	}),
	withComputed((state) => {
		return {
			filteredCart: computed(() => {
				return state
					.cart()
					.filter((item: Selectable<EnrichedCartItem>) => {
						if (state.filters().includes(CartOtherFilterEnum.MarkedForDeletion)) {
							return Helper.calculateSoonToBeDeletedFlag(item.created);
						} else {
							return true;
						}
					})
					.filter((item: Selectable<EnrichedCartItem>) => {
						if (
							state.filters().includes(CartOrderTypeFilterEnum.ReplacementOrder) &&
							state.filters().includes(CartOrderTypeFilterEnum.NewOrder)
						) {
							return true;
						} else if (state.filters().includes(CartOrderTypeFilterEnum.ReplacementOrder)) {
							return item.orderType === 'ECPReplacement';
						} else if (state.filters().includes(CartOrderTypeFilterEnum.NewOrder)) {
							return item.orderType === 'ECPOrder';
						} else {
							return true;
						}
					});
			}),
			selectedCartItems: computed(() =>
				state.cart().filter((item: Selectable<EnrichedCartItem>) => item.isSelected),
			),
		};
	}),
);

export function mapFiltersToDateRange(filters: CartFilters[]): DateRange | null {
	let dateRangeToReturn: DateRange | null = null;
	// this filter includes all other filters
	if (filters.includes(CartTimeFilterEnum.Last5Days)) {
		let startDate = new Date();
		startDate.setDate(startDate.getDate() - 5);
		dateRangeToReturn = {
			startDate,
			endDate: new Date(),
		};
	} else {
		if (filters.includes(CartTimeFilterEnum.Today)) {
			let startDate = new Date();
			startDate.setHours(0, 0, 0, 0);
			let endDate = new Date();
			endDate.setHours(23, 59, 59, 999);
			dateRangeToReturn = {
				startDate,
				endDate,
			};
		}
		if (filters.includes(CartTimeFilterEnum.Yesterday)) {
			let today = new Date();
			let yesterday = new Date().setDate(today.getDate() - 1);
			let startDate = new Date(yesterday);
			startDate.setHours(0, 0, 0, 0);
			let endDate = new Date(yesterday);
			endDate.setHours(23, 59, 59, 999);
			dateRangeToReturn = {
				startDate,
				endDate: dateRangeToReturn ? dateRangeToReturn.endDate : endDate,
			};
		}
	}
	return dateRangeToReturn;
}
